const GEMINI_KEY = process.env.REACT_APP_GEMINI_KEY || '';
// "gemini-flash-latest" é um alias que sempre aponta pro modelo flash estável mais
// recente — evita quebrar quando a Google descontinuar uma versão específica (ex: 1.5-flash)
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${GEMINI_KEY}`;

// Compila os dados financeiros reais do usuário para enviar ao Gemini
export function buildFinancialContext(state) {
  const now = new Date();
  const thisMonth = now.toISOString().slice(0, 7);
  const last3Months = [0, 1, 2].map(i => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });

  const txs = (state.transactions || []).filter(t =>
    last3Months.some(m => (t.date || '').startsWith(m))
  );

  const thisMonthTxs = txs.filter(t => (t.date || '').startsWith(thisMonth));

  // Receita e despesa do mês atual
  const monthIncome  = thisMonthTxs.filter(t => t.type === 'income').reduce((s, t) => s + (t.value || 0), 0);
  const monthExpense = thisMonthTxs.filter(t => t.type === 'expense').reduce((s, t) => s + (t.value || 0), 0);

  // Gastos por categoria (mês atual)
  const byCategory = {};
  thisMonthTxs.filter(t => t.type === 'expense').forEach(t => {
    const cat = t.category || 'Outros';
    byCategory[cat] = (byCategory[cat] || 0) + (t.value || 0);
  });

  const categoryList = Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, val]) => `  - ${cat}: R$ ${val.toFixed(2)} (${monthIncome > 0 ? ((val / monthIncome) * 100).toFixed(1) : 0}% da renda)`)
    .join('\n');

  // Gastos fixos vs variáveis (recorrentes = fixos)
  const recurringDesc = new Set();
  const recurringTxs = txs.filter(t => {
    if (t.type !== 'expense') return false;
    const key = (t.description || '').toLowerCase().trim();
    if (recurringDesc.has(key)) return true;
    recurringDesc.add(key);
    return false;
  });
  const fixedTotal = recurringTxs.reduce((s, t) => s + (t.value || 0), 0) / 3;

  // Metas
  const goals = (state.goals || []).slice(0, 5).map(g =>
    `  - ${g.name}: meta R$ ${(g.targetValue || 0).toFixed(2)}, guardado R$ ${(g.currentValue || 0).toFixed(2)} (${g.targetValue > 0 ? (((g.currentValue || 0) / g.targetValue) * 100).toFixed(0) : 0}%)`
  ).join('\n');

  // Dívidas
  const debts = (state.debts || []).filter(d => d.status !== 'paid').slice(0, 5).map(d =>
    `  - ${d.name}: total R$ ${(d.totalValue || 0).toFixed(2)}, restante R$ ${(d.remainingValue || d.totalValue || 0).toFixed(2)}, parcela R$ ${(d.installment || 0).toFixed(2)}/mês`
  ).join('\n');

  // Contas
  const accounts = (state.accounts || []).slice(0, 5).map(a =>
    `  - ${a.name}: saldo R$ ${(a.balance || 0).toFixed(2)}`
  ).join('\n');

  const savingsGoal = state.savingsGoalMonthly || 0;
  const currentSavings = Math.max(0, monthIncome - monthExpense);

  return `
=== DADOS FINANCEIROS REAIS DO USUÁRIO (MAW Finanças) ===

Mês de referência: ${now.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
Renda líquida estimada este mês: R$ ${monthIncome.toFixed(2)}
Total de despesas este mês: R$ ${monthExpense.toFixed(2)}
Saldo do mês (receita - despesa): R$ ${currentSavings.toFixed(2)}
Meta de economia mensal definida: R$ ${savingsGoal.toFixed(2)}

GASTOS POR CATEGORIA (mês atual):
${categoryList || '  (nenhum gasto registrado ainda)'}

ESTIMATIVA GASTOS FIXOS (média últimos 3 meses): R$ ${fixedTotal.toFixed(2)}
ESTIMATIVA GASTOS VARIÁVEIS: R$ ${Math.max(0, monthExpense - fixedTotal).toFixed(2)}

CONTAS E SALDOS:
${accounts || '  (nenhuma conta cadastrada)'}

METAS FINANCEIRAS:
${goals || '  (nenhuma meta cadastrada)'}

DÍVIDAS ATIVAS:
${debts || '  (nenhuma dívida cadastrada)'}

=== FIM DOS DADOS ===

Baseando-se EXCLUSIVAMENTE nesses dados reais acima, responda em português brasileiro de forma clara, prática e personalizada. Use os valores reais fornecidos. Seja direto e objetivo.
`.trim();
}

// Lê um PDF de "Proventos Previstos" exportado da corretora e extrai a tabela
// em JSON estruturado. Funciona com qualquer corretora — o Gemini lê o layout
// da tabela direto do PDF, não depende de um formato fixo.
export async function parseProventosPdf(base64Pdf) {
  if (!GEMINI_KEY) throw new Error('API key não configurada');

  const prompt = `Este PDF é um extrato de "Proventos Previstos" de uma corretora de investimentos brasileira (dividendos/JCP/rendimentos futuros já anunciados, ainda não pagos).

Extraia CADA LINHA da tabela e devolva um array JSON, um objeto por linha, neste formato exato:
[{"ticker":"PETR4","valorLiquido":11.56,"data":"2026-08-20","tipo":"JCP"}]

Regras:
- "ticker": o código do ativo (coluna TICKER), em maiúsculas, sem espaços.
- "valorLiquido": o valor líquido previsto (procure a coluna "VALOR LÍQUIDO PREVISTO" ou "VALOR LÍQUIDO" — é o valor já descontado do IR, não o valor provisionado bruto), como número (use ponto decimal, não vírgula).
- "data": a data de liquidação/pagamento (coluna "DATA DE LIQUIDAÇÃO" ou "DATA PAGAMENTO"), no formato YYYY-MM-DD.
- "tipo": "Dividendo" se a descrição do provento for "DIVIDENDO", "JCP" se for "JUROS SOBRE CAPITAL PROPRIO", "Rendimento" se for rendimento de FII, caso contrário use o texto original resumido.

Devolva SOMENTE o array JSON, sem markdown, sem texto antes ou depois.`;

  const res = await fetch(GEMINI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [
        { inline_data: { mime_type: 'application/pdf', data: base64Pdf } },
        { text: prompt },
      ] }],
      generationConfig: {
        temperature: 0,
        maxOutputTokens: 4096,
        // Sem thinkingConfig: o gemini-flash-latest atual rejeita thinkingBudget:0 (HTTP 400)
      },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Erro ${res.status}`);
  }

  const data = await res.json();
  const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
  const cleaned = text.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '');
  try {
    const rows = JSON.parse(cleaned);
    if (!Array.isArray(rows)) throw new Error('Formato inesperado');
    return rows;
  } catch (e) {
    throw new Error('Não consegui ler a tabela do PDF. Tente novamente ou confira se o arquivo está certo.');
  }
}

// Sugestão de categoria pra descrição digitada no lançamento — usada só como
// FALLBACK quando o histórico do usuário não tem nada parecido (ver
// TransactionModal). Cache por descrição pra nunca chamar a API duas vezes
// pela mesma coisa. Falha em silêncio: sem sugestão é melhor que erro na tela.
const catSuggestionCache = {};
export async function suggestCategoryAI(description, categories) {
  if (!GEMINI_KEY) return null;
  const key = description.trim().toLowerCase();
  if (key in catSuggestionCache) return catSuggestionCache[key];
  try {
    const list = categories.map(c => `${c.id}: ${c.name}${(c.subcategories || []).length ? ` (subcategorias: ${c.subcategories.join(', ')})` : ''}`).join('\n');
    const prompt = `Classifique o gasto/receita "${description}" em UMA das categorias abaixo (contexto: finanças pessoais no Brasil).\n\n${list}\n\nResponda SOMENTE com JSON puro: {"categoryId":"<id da lista>","subcategory":"<subcategoria exata da lista ou string vazia>"}`;
    const res = await fetch(GEMINI_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: { temperature: 0, maxOutputTokens: 100 },
      }),
    });
    if (!res.ok) { catSuggestionCache[key] = null; return null; }
    const data = await res.json();
    const raw = (data?.candidates?.[0]?.content?.parts?.[0]?.text || '').replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(raw);
    const cat = categories.find(c => c.id === parsed.categoryId);
    if (!cat) { catSuggestionCache[key] = null; return null; }
    const sub = (cat.subcategories || []).includes(parsed.subcategory) ? parsed.subcategory : '';
    const result = { categoryId: cat.id, subcategory: sub };
    catSuggestionCache[key] = result;
    return result;
  } catch {
    catSuggestionCache[key] = null;
    return null;
  }
}

export async function askGemini(prompt, financialContext) {
  if (!GEMINI_KEY) throw new Error('API key não configurada');

  const fullPrompt = `${financialContext}\n\n${prompt}`;

  const res = await fetch(GEMINI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: fullPrompt }] }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 1500,
      },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Erro ${res.status}`);
  }

  const data = await res.json();
  return data?.candidates?.[0]?.content?.parts?.[0]?.text || 'Sem resposta.';
}
